import { makeAutoObservable } from "mobx";
import type AppStore from "../AppStore";
import { type AxiosResponse, type AxiosInstance } from "axios";
import { CachedClient, LazyAggregates, setFromJSON, omitNull } from "helpers";
import {
  type JSONObject,
  type Address,
  type BuildingSimulation,
  type BuildingSimulationAggregate,
  type BuildingUserData,
  type ConsumptionSimulation,
  type HeatConsumptionSimulation,
} from "stores/@types";
import {
  type AppFeature,
  FeatureType,
  type MultiFeature,
  type Project,
  type WriteFeature,
  type ParentFeature,
} from "stores/features";

const BUILDING_AGGREGATES = {
  usable_area: "sum",
  area: "sum",
  heat_demand: "sum",
  power_demand: "sum",
  residents: "sum",
  heat_consumption_kWh: "sum",
  power_consumption_kWh: "sum",
  heat_co2_emission: "sum",
  power_co2_emission: "sum",
  heat_energy_cost: "sum",
  power_energy_cost: "sum",
  solar_potential: "sum",
  heat_demand_renovation: "sum",
  reference_area: "sum",
  heat_quality: "avg",
  power_quality: "avg",
  heatsupply: "sum",
  solar_heat: "sum",
  solar_power: "sum",
  heat_primary_consumption: "sum",
  power_primary_consumption: "sum",
  heat_ultimate_demand: "sum",
  power_ultimate_demand: "sum",
  heat_co2_cost: "sum",
  power_co2_cost: "sum",
  powersupply: "sum",
  renovation_cost: "sum",
  heat_consumption_or_demand_kwh: "sum",
  power_consumption_or_demand_kwh: "sum",
  power_consumption_user_kwh: "sum",
  heat_consumption_user_kwh: "sum",
  heat_consumption_user_climate_adjusted_kwh: "sum",
};

interface HeatSupplyAggregate {
  heatsupply: number;
  heat_consumption_or_demand_kwh__sum: number;
}

interface PowerSupplyAggregate {
  powersupply: number;
  power_consumption_or_demand_kwh__sum: number;
}

interface BiskoAggregate {
  bisko: number;
  power_consumption_or_demand_kwh__sum: number;
  heat_consumption_or_demand_kwh__sum: number;
}

export class HeatConsumption {
  public readonly uuid?: string;
  public supply?: number;
  public unit?: number;
  public consumption?: number;
  public year?: number;

  constructor(private readonly building: Building) {}

  static fromJSON(
    building: Building,
    data: HeatConsumptionSimulation,
  ): SimulatedHeatConsumption {
    const consumption = new HeatConsumption(building);
    setFromJSON(consumption, data, ["building"]);
    return consumption as SimulatedHeatConsumption;
  }

  get client(): AxiosInstance {
    return this.building.client;
  }

  delete(): Promise<AxiosResponse> | null {
    if (this.uuid === undefined) return null;
    const promise = this.client.delete(`heatconsumption/${this.uuid}/`);
    void promise.then(() => {
      this.building.resetHeatConsumptions();
    });
    return promise;
  }

  async save(): Promise<AxiosResponse> {
    let promise: Promise<AxiosResponse>;
    if (this.uuid === undefined) {
      promise = this.client.post(`heatconsumption/`, {
        building: this.building.uuid,
        year: this.year,
        supply: this.supply,
        unit: this.unit,
        consumption: this.consumption,
      });
      void promise.then(() => {
        this.building.resetHeatConsumptions();
      });
    } else {
      promise = this.client.patch(`heatconsumption/${this.uuid}/`, {
        building: this.building.uuid,
        year: this.year,
        supply: this.supply,
        unit: this.unit,
        consumption: this.consumption,
      });
    }
    void promise.then((response) => {
      setFromJSON(this, response.data, ["building"]);
    });
    return await promise;
  }
}

export interface SimulatedHeatConsumption extends HeatConsumption {
  readonly uuid: string;
  supply: number;
  unit: number;
  consumption: number;
  year: number;
}

export class PowerConsumption {
  public readonly uuid?: string;
  public supply?: number;
  public unit?: number;
  public consumption?: number;
  public year?: number;

  constructor(private readonly building: Building) {}

  static fromJSON(
    building: Building,
    data: ConsumptionSimulation,
  ): SimulatedPowerConsumption {
    const consumption = new PowerConsumption(building);
    setFromJSON(consumption, data, ["building"]);
    return consumption as SimulatedPowerConsumption;
  }

  get client(): AxiosInstance {
    return this.building.client;
  }

  delete(): Promise<AxiosResponse> | null {
    if (this.uuid === undefined) return null;
    const promise = this.client.delete(`powerconsumption/${this.uuid}/`);
    void promise.then(() => {
      this.building.resetPowerConsumptions();
    });
    return promise;
  }

  async save(): Promise<AxiosResponse> {
    let promise: Promise<AxiosResponse>;
    if (this.uuid === undefined) {
      promise = this.client.post(`powerconsumption/`, {
        building: this.building.uuid,
        year: this.year,
        supply: this.supply,
        unit: this.unit,
        consumption: this.consumption,
      });
    } else {
      promise = this.client.patch(`powerconsumption/${this.uuid}/`, {
        building: this.building.uuid,
        year: this.year,
        supply: this.supply,
        unit: this.unit,
        consumption: this.consumption,
      });
    }
    void promise.then(() => {
      this.building.resetPowerConsumptions();
    });
    return await promise;
  }
}

export interface SimulatedPowerConsumption extends PowerConsumption {
  readonly uuid: string;
  supply: number;
  unit: number;
  consumption: number;
  year: number;
}

export class Building implements AppFeature {
  static heatConsumptionCacheKey = "heatConsumptions";
  static powerConsumptionCacheKey = "powerConsumptions";
  public readonly type = FeatureType.Building;
  private readonly cached: CachedClient;
  public readonly isEditingSupported: boolean = true;

  constructor(
    private readonly project: Project,
    public uuid: string,
  ) {
    makeAutoObservable(this, { uuid: false });
    this.cached = new CachedClient(this);
  }

  get client(): AxiosInstance {
    return this.project.client;
  }

  get title(): string {
    return this.address?.text ?? "Ein Objekt ausgewählt";
  }

  get address(): Address | undefined {
    return this.cached.get<Address[], Address>(
      `address/?building=${this.uuid}`,
      undefined,
      (data) => data[0],
    );
  }

  get simulation(): BuildingSimulation | undefined {
    return this.cached.get<BuildingSimulation>(
      `buildingsimulation/${this.uuid}/`,
    );
  }

  get userData(): BuildingUserData | undefined {
    return this.cached.get<BuildingUserData>(`building/${this.uuid}/`);
  }

  get heatConsumptions(): SimulatedHeatConsumption[] | undefined {
    return this.cached.get<
      HeatConsumptionSimulation[],
      SimulatedHeatConsumption[]
    >(
      `heatconsumption/?building=${this.uuid}`,
      Building.heatConsumptionCacheKey,
      (data) =>
        data
          .map((el) => HeatConsumption.fromJSON(this, el))
          .slice()
          .sort((a, b) => b.year - a.year),
    );
  }

  resetHeatConsumptions(): void {
    this.cached.reset([Building.heatConsumptionCacheKey]);
  }

  get powerConsumptions(): PowerConsumption[] | undefined {
    return this.cached.get<ConsumptionSimulation[], PowerConsumption[]>(
      `powerconsumption/?building=${this.uuid}`,
      Building.powerConsumptionCacheKey,
      (data) =>
        data
          .map((el) => PowerConsumption.fromJSON(this, el))
          .slice()
          .sort((a, b) => b.year - a.year),
    );
  }

  resetPowerConsumptions(): void {
    this.cached.reset([Building.powerConsumptionCacheKey]);
  }

  get biskoTitle(): string | undefined {
    return this.simulation?.bisko === undefined
      ? undefined
      : this.project.store.getBiskoTitle(this.simulation?.bisko);
  }

  get monitorData(): JSONObject | undefined {
    return this.simulation;
  }

  get formPlaceHolder(): JSONObject | undefined {
    return { ...this.userData, ...this.simulation };
  }

  async patch(data: BuildingUserData): Promise<AxiosResponse> {
    const promise = this.project.client.patch(`building/${this.uuid}/`, data);
    void promise.then(() => {
      this.project.store.bumpBuildingVersion();
      this.cached.reset();
    });
    return await promise;
  }

  reset(): void {
    this.cached.reset();
  }
}

export class MultiBuilding implements MultiFeature, WriteFeature {
  public readonly type = FeatureType.Building;
  private readonly cached: CachedClient;

  public readonly aggregates: LazyAggregates;

  constructor(public readonly parent: ParentFeature) {
    this.aggregates = new LazyAggregates(this, "buildingsimulation");
    this.cached = new CachedClient(this.parent);
    makeAutoObservable(this);
  }

  reset(): void {
    this.cached.reset();
    this.aggregates.reset();
  }

  get count(): number | undefined {
    return this.aggregates.aggregate({ uuid: "count" })?.uuid__count;
  }

  get simulation(): BuildingSimulationAggregate | undefined {
    return this.aggregates.aggregate(BUILDING_AGGREGATES) as
      | BuildingSimulationAggregate
      | undefined;
  }

  get sample(): Building | null | undefined {
    return this.cached.get<BuildingSimulation[], Building | null>(
      `buildingsimulation/?${this.parent.filter}limit=1`,
      undefined,
      (data) => {
        const sampleData = data[0];
        if (sampleData === undefined) return null;
        return new Building(this.parent.store.selectedProject, data[0].uuid);
      },
    );
  }

  get heatSupplyAggregation(): HeatSupplyAggregate[] | undefined {
    return this.aggregates.groupBy(
      {
        heat_consumption_or_demand_kwh: "sum",
      },
      ["heatsupply"],
    ) as HeatSupplyAggregate[] | undefined;
  }

  get powerSupplyAggregation(): PowerSupplyAggregate[] | undefined {
    return this.aggregates.groupBy(
      {
        power_consumption_or_demand_kwh: "sum",
      },
      ["powersupply"],
    ) as PowerSupplyAggregate[] | undefined;
  }

  get biskoAggregation(): BiskoAggregate[] | undefined {
    return this.aggregates.groupBy(
      {
        heat_consumption_or_demand_kwh: "sum",
        power_consumption_or_demand_kwh: "sum",
        heat_demand: "sum",
        power_demand: "sum",
      },
      ["bisko"],
    ) as BiskoAggregate[] | undefined;
  }

  get biskoSectorTitles(): string {
    return (
      this.biskoAggregation
        ?.map((el) => this.parent.store.getBiskoTitle(el.bisko))
        .join(", ") ?? ""
    );
  }

  async patch(data: BuildingUserData): Promise<AxiosResponse> {
    const headers = {
      headers: {
        "eep-objects-to-patch-count":
          this.parent instanceof BuildingCollection
            ? this.parent.uuids.length
            : this.parent.buildings?.count,
      },
    };
    const promise = this.parent.client
      .patch(`building/bulk/?${this.parent.filter}`, data, headers)
      .catch((err) => {
        return err;
      });
    void promise.then(() => {
      this.reset();
    });
    return await promise;
  }

  get formPlaceHolder(): JSONObject | undefined {
    const sample = this.sample;
    if (
      sample?.simulation === undefined ||
      sample?.userData === undefined ||
      sample === undefined
    )
      return;

    return { ...omitNull(sample?.simulation), ...omitNull(sample?.userData) };
  }
}

export class BuildingCollection implements AppFeature {
  public readonly type = FeatureType.Building;
  public buildings: MultiBuilding;
  public store: AppStore;
  public readonly isEditingSupported: boolean = true;

  constructor(
    private readonly project: Project,
    public uuids: string[],
  ) {
    this.buildings = new MultiBuilding(this);
    makeAutoObservable(this);
    this.store = project.store;
  }

  reset(): void {
    this.buildings.reset();
  }

  get client(): AxiosInstance {
    return this.project.client;
  }

  get title(): string {
    return `${this.uuids.length} Gebäude ausgewählt`;
  }

  get filter(): string {
    return `uuid__in=${this.uuids.join(",")}&`;
  }

  get monitorData(): JSONObject | undefined {
    return this.buildings.simulation;
  }

  get formPlaceHolder(): JSONObject | undefined {
    return this.buildings.formPlaceHolder;
  }

  async patch(data: BuildingUserData): Promise<AxiosResponse> {
    return await this.buildings.patch(data);
  }
}
